import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../../apiClients/workspace/types';
import { getLevelIcon } from '../../lib/userUtils';
import { useSkillLevelsStore, useUserStore } from '../../stores';

interface UserSkillLevelCardProps {
  user: User;
  isAdmin: boolean;
  isCurrentUser: boolean;
}

const UserSkillLevelCard: React.FC<UserSkillLevelCardProps> = observer(
  ({ user, isAdmin }) => {
    const { t } = useTranslation();
    const userStore = useUserStore();
    const skillLevelsStore = useSkillLevelsStore();

    const [isEditing, setIsEditing] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(user.skillLevel || 1);
    const [isSaving, setIsSaving] = useState(false);

    const handleEdit = () => {
      if (isAdmin) {
        setIsEditing(true);
        setSelectedLevel(user.skillLevel || 1);
      }
    };

    const handleSave = async () => {
      if (selectedLevel === user.skillLevel) {
        setIsEditing(false);
        return;
      }

      setIsSaving(true);
      try {
        await userStore.updateById(user.id, { skillLevel: selectedLevel });
        setIsEditing(false);
      } catch (error) {
        console.error('Failed to update skill level:', error);
      } finally {
        setIsSaving(false);
      }
    };

    const handleCancel = () => {
      setSelectedLevel(user.skillLevel || 1);
      setIsEditing(false);
    };

    // Always show the card, even if skillLevel is null
    return (
      <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
        {!isEditing ? (
          <div
            className={`flex items-center space-x-3 ${isAdmin ? 'cursor-pointer hover:bg-blue-100 transition-colors rounded-lg p-2 -m-2' : ''}`}
            onClick={handleEdit}
          >
            <span className="text-2xl">
              {user.skillLevel
                ? getLevelIcon(
                    userStore.getUserSkillLevelWeight(user.id),
                    userStore.getUserSkillLevelIconId(user.id)
                  )
                : '❓'}
            </span>
            <div className="flex-1">
              <p className="font-semibold text-blue-900 text-lg">
                {user.skillLevel
                  ? t(userStore.getUserSkillLevel(user.id) ?? '-')
                  : t('profile.noSkillLevel')}
              </p>
              <p className="text-sm text-blue-700">{t('profile.skillLevel')}</p>
            </div>
            {isAdmin && <span className="text-blue-600 text-sm">✏️</span>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                {user.skillLevel
                  ? getLevelIcon(
                      userStore.getUserSkillLevelWeight(user.id),
                      userStore.getUserSkillLevelIconId(user.id)
                    )
                  : '❓'}
              </span>
              <div>
                <p className="font-semibold text-blue-900 text-lg">
                  {t('profile.selectNewLevel')}
                </p>
                <p className="text-sm text-blue-700">
                  {t('profile.skillLevel')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {skillLevelsStore.levelsArray.map((level) => (
                <button
                  key={level.weight}
                  type="button"
                  onClick={() => setSelectedLevel(level.weight)}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                    selectedLevel === level.weight
                      ? 'bg-blue-100 border-blue-300 text-blue-900'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xl">
                    {getLevelIcon(level.weight, level.settings?.iconId)}
                  </span>
                  <div className="text-left">
                    <p className="font-medium text-sm">{level.name}</p>
                    <p className="text-xs text-gray-500">
                      {t('profile.level')} {level.weight}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end space-x-3 pt-3 border-t border-blue-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || selectedLevel === user.skillLevel}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default UserSkillLevelCard;
